import { FieldArray, useFormikContext } from "formik";
import React, { ReactNode } from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicIconButton, ButtonType } from "@gaming/xds-react-js";
import { Add, Close } from "@gaming/xds-react-js-icons";

import { FieldErrorMessage, FieldHeader, FieldHeaderProps } from "../";
import { useString } from "../../localization/localizationHelpers";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { useSetFieldTouched } from "../useSetFieldTouched";
import * as styles from "./DynamicObjectListField.module.css";

type DynamicObjectListFieldProps = Readonly<{
  name: string;
  buttonLabel: string;
  renderChild: (name: string, index: number) => ReactNode;
  disabled?: boolean;
  isRequiredField?: boolean;
  hasLabels?: boolean;
  disableAddNewUrlButton?: boolean;
  customHeader?: ReactNode;
}> &
  FieldHeaderProps;

export const DynamicObjectListField = (props: DynamicObjectListFieldProps) => {
  const removeLinkButtonText = useString("removeLinkButtonText");
  const {
    name,
    buttonLabel,
    renderChild,
    disabled,
    isRequiredField,
    hasLabels,
    disableAddNewUrlButton,
    customHeader,
  } = props;
  const { errors } = useFormikContext();
  useSetFieldTouched(name);

  const addNewUrlRow = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    addNewRow: (_value: string) => void
  ) => {
    e.preventDefault();
    addNewRow("");
  };
  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        {customHeader || <FieldHeader {...props} />}
        <FieldArray
          name={name}
          render={({ push, form, remove }) => (
            <>
              {form.values[name].length > 0 &&
                form.values[name].map((_: string, index: number) => (
                  <div className={styles.rowContainer} key={index}>
                    {renderChild(name, index)}
                    {(!isRequiredField || index !== 0) && (
                      <div className={styles.removeButtonContainer}>
                        <BasicIconButton
                          as={"button"}
                          buttonType={ButtonType.Primary}
                          onClick={() => remove(index)}
                          className={hasLabels ? styles.removeLinkLabel : styles.removeLink}
                          icon={Close}
                          disabled={disabled}
                          aria-label={removeLinkButtonText}
                        />
                      </div>
                    )}
                  </div>
                ))}
              {!disableAddNewUrlButton && (
                <Row>
                  <Column>
                    <BasicIconButton
                      as={"button"}
                      buttonType={ButtonType.Primary}
                      onClick={e => addNewUrlRow(e, push)}
                      className={styles.addLink}
                      icon={Add}
                      disabled={disabled}
                      title={buttonLabel}
                      aria-label={buttonLabel}
                    >
                      {buttonLabel}
                    </BasicIconButton>
                  </Column>
                </Row>
              )}
              {errors[name] && typeof errors[name] === "string" && (
                <Row className={styles.addLinkFieldError}>
                  <FieldErrorMessage fieldName={name} />
                </Row>
              )}
            </>
          )}
        />
      </Column>
    </Row>
  );
};
